import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

// Admin header menu (see Layout.js / .admin-layout-header in Layout.css).
// Shows every item that fits on one row as icon + label; the rest move into a
// "More" dropdown instead of being clipped. Items that do not fit stay in the
// row hidden (visibility: hidden, out of flow) so their width can be measured.
//
// items: [{ key, className, isActive, onClick, icon, label, badge, disabled }]
// A disabled item is shown greyed out and does nothing when clicked.

const DISABLED_TITLE = "Coming soon";

const MORE_GAP = 4;

export default function AdminTopMenu({ items }) {
  const wrapRef = useRef(null);
  const rowRef = useRef(null);
  const moreRef = useRef(null);
  const [fitCount, setFitCount] = useState(items.length);
  const [moreOpen, setMoreOpen] = useState(false);

  // Re-measure when the items (or a badge, which changes an item's width) change.
  const itemsKey = items.map((item) => `${item.key}:${item.label}:${item.badgeText || ""}`).join("|");

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const row = rowRef.current;
    if (!wrap || !row) return undefined;

    const measure = () => {
      const widths = [...row.children].map((child) => child.offsetWidth);
      const rowStyle = window.getComputedStyle(row);
      const gap = Number.parseFloat(rowStyle.columnGap) || 0;
      // The row's own margin/padding (partly from global .scrollable-menu rules) takes room too.
      const rowSpacing = ["marginLeft", "marginRight", "paddingLeft", "paddingRight"].reduce(
        (sum, prop) => sum + (Number.parseFloat(rowStyle[prop]) || 0),
        0
      );
      const available = wrap.clientWidth - rowSpacing;
      const rowWidth = (count) =>
        widths.slice(0, count).reduce((sum, width, index) => sum + width + (index ? gap : 0), 0);

      if (rowWidth(widths.length) <= available) {
        setFitCount(widths.length);
        return;
      }
      const moreWidth = (moreRef.current?.offsetWidth || 90) + MORE_GAP;
      let count = widths.length;
      while (count > 0 && rowWidth(count) + moreWidth > available) count -= 1;
      setFitCount(count);
    };

    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(wrap);
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => observer?.disconnect();
  }, [itemsKey]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "mousedown" && moreRef.current?.contains(event.target)) return;
      setMoreOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [moreOpen]);

  const overflowItems = items.slice(fitCount);
  const hasOverflow = overflowItems.length > 0;
  const overflowActive = overflowItems.some((item) => item.isActive);

  return (
    <div ref={wrapRef} className="admin-top-menu">
      <div ref={rowRef} className="top-menu scrollable-menu">
        {items.map((item, index) => {
          const Icon = item.icon;
          const hidden = index >= fitCount;
          return (
            <div
              key={item.key}
              className={`${item.className}${hidden ? " is-overflow" : ""}`}
              onClick={item.onClick}
              aria-hidden={hidden || undefined}
              aria-disabled={item.disabled || undefined}
              title={item.disabled ? DISABLED_TITLE : undefined}
            >
              {Icon && <Icon className="menu-item-icon" size={18} aria-hidden="true" />}
              {item.label}
              {item.badge}
            </div>
          );
        })}
      </div>

      <div ref={moreRef} className={`admin-menu-more${hasOverflow ? "" : " is-overflow"}`}>
        <button
          type="button"
          className={`admin-menu-more-toggle${overflowActive ? " active" : ""}`}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((open) => !open)}
          tabIndex={hasOverflow ? 0 : -1}
        >
          More
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        {moreOpen && hasOverflow && (
          <div className="admin-menu-more-panel" role="menu">
            {overflowItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={item.isActive ? "active" : ""}
                  disabled={item.disabled}
                  title={item.disabled ? DISABLED_TITLE : undefined}
                  onClick={() => {
                    setMoreOpen(false);
                    item.onClick();
                  }}
                >
                  {Icon && <Icon size={18} aria-hidden="true" />}
                  <span>{item.label}</span>
                  {item.badge}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
